import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { ROUTES } from '@/config/routes';
import {
  onboardingService,
  type AccessStatusResponse,
} from '@/services/onboardingService';
import { extractOrganizationId } from '@/utils/tokenClaims';

interface OnboardingGuardProps {
  children?: React.ReactNode;
}

interface HttpErrorShape {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

interface TokenWithRoles {
  realm_access?: {
    roles?: string[];
  };
  organizationId?: string;
  organizationName?: string;
  organizationStatus?: string;
  organization_status?: string;
  org_status?: string;
  resource_access?: Record<
    string,
    {
      roles?: string[];
    }
  >;
}

const ONBOARDING_PATHS = [
  ROUTES.NO_ORGANIZATION,
  ROUTES.ORG_REGISTRATION,
  ROUTES.WAITING_APPROVAL,
  ROUTES.REJECTED_ORGANIZATION,
];
const ONBOARDING_PENDING_KEY = 'kibo:onboarding-pending';

function normalizeTokenState(state?: string): AccessStatusResponse['state'] {
  const value = (state ?? '').toLowerCase();
  if (value === 'approved' || value === 'active') {
    return 'approved';
  }
  if (value === 'pending' || value === 'pending_approval') {
    return 'pending_approval';
  }
  if (value === 'rejected') {
    return 'rejected';
  }
  if (value === 'suspended') {
    return 'suspended';
  }
  return 'no_organization';
}

function deriveFallbackStatus(tokenParsed: unknown): AccessStatusResponse {
  const token = (tokenParsed ?? {}) as TokenWithRoles;
  const realmRoles = token.realm_access?.roles ?? [];
  const clientRoles = Object.values(token.resource_access ?? {}).flatMap(
    (client) => client.roles ?? [],
  );
  const allRoles = new Set([...realmRoles, ...clientRoles]);
  const hasPendingOnboarding =
    typeof window !== 'undefined' && window.localStorage.getItem(ONBOARDING_PENDING_KEY) === '1';

  if (allRoles.has('platform_admin')) {
    return {
      state: 'approved',
      isPlatformAdmin: true,
      organization: null,
      requiresReauthentication: false,
    };
  }

  const organizationId = extractOrganizationId(token as Record<string, unknown>);
  const organizationName = (token.organizationName ?? 'Organization').trim();
  const tokenState = normalizeTokenState(
    token.organizationStatus ?? token.organization_status ?? token.org_status,
  );

  if (organizationId) {
    return {
      state: tokenState === 'no_organization' ? 'approved' : tokenState,
      isPlatformAdmin: false,
      organization: {
        id: organizationId,
        name: organizationName || 'Organization',
        status: tokenState === 'suspended' ? 'SUSPENDED' : 'ACTIVE',
      },
      requiresReauthentication: false,
    };
  }

  if (tokenState === 'rejected') {
    return {
      state: 'rejected',
      isPlatformAdmin: false,
      organization: null,
      requiresReauthentication: false,
    };
  }

  if (tokenState === 'pending_approval' || hasPendingOnboarding) {
    return {
      state: 'pending_approval',
      isPlatformAdmin: false,
      organization: null,
      requiresReauthentication: false,
    };
  }

  return {
    state: 'no_organization',
    isPlatformAdmin: false,
    organization: null,
    requiresReauthentication: false,
  };
}

function Loader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ErrorState({
  onRetry,
  message,
}: {
  onRetry: () => void;
  message: string;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="max-w-md rounded-xl border border-border bg-card p-6 text-center">
        <h1 className="text-xl font-semibold text-foreground">Unable to verify access state</h1>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { keycloak, initialized } = useKeycloak();
  const location = useLocation();
  const hasAttemptedRecovery = useRef(false);

  const isPlatformAdmin = useMemo(() => {
    const token = (keycloak.tokenParsed ?? {}) as TokenWithRoles;
    const realmRoles = token.realm_access?.roles ?? [];
    const clientRoles = Object.values(token.resource_access ?? {}).flatMap(
      (client) => client.roles ?? [],
    );
    const allRoles = new Set([...realmRoles, ...clientRoles]);
    return allRoles.has('platform_admin');
  }, [keycloak.tokenParsed]);

  const [status, setStatus] = useState<AccessStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    'We could not determine your organization onboarding status. Please try again.',
  );

  const isOnboardingRoute = useMemo(
    () => ONBOARDING_PATHS.includes(location.pathname as (typeof ONBOARDING_PATHS)[number]),
    [location.pathname],
  );

  const fetchStatus = useCallback(async (retryCount = 0) => {
    setHasError(false);
    setLoading(true);
    try {
      const result = await onboardingService.getAccessStatus();
      if (result.state === 'approved') {
        window.localStorage.removeItem(ONBOARDING_PENDING_KEY);
      }
      setStatus(result);
    } catch (error: unknown) {
      const err = error as HttpErrorShape;
      const statusCode = err.response?.status;

      if (statusCode === 401 || statusCode === 403) {
        if (!hasAttemptedRecovery.current) {
          hasAttemptedRecovery.current = true;
          try {
            await keycloak.updateToken(0);
            const retryResult = await onboardingService.getAccessStatus();
            if (retryResult.state === 'approved') {
              window.localStorage.removeItem(ONBOARDING_PENDING_KEY);
            }
            setStatus(retryResult);
            hasAttemptedRecovery.current = false;
            return;
          } catch {
            // Fall through to user-facing error state instead of initiating a login loop.
          } finally {
            hasAttemptedRecovery.current = false;
          }
        }
      }

      // Retry up to 2 times with a brief backoff for temporary gateway / upstream errors (502, 503, 504, or network blips)
      if ((!statusCode || statusCode >= 500) && retryCount < 2) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        return fetchStatus(retryCount + 1);
      }

      // Fall back to token-derived onboarding state ONLY if the token itself contains approved org
      const tokenFallback = deriveFallbackStatus(keycloak.tokenParsed);
      if (tokenFallback.state === 'approved' || tokenFallback.organization?.id) {
        setStatus(tokenFallback);
        setHasError(false);
        return;
      }

      // If already on an onboarding route, let fallback state guide the step
      if (isOnboardingRoute) {
        setStatus(tokenFallback);
        setHasError(false);
        return;
      }

      // Never silently kick an authenticated user to "no_organization" on server/network errors
      if (!statusCode || statusCode >= 500) {
        setErrorMessage(
          'Connecting to authentication services... Please click Retry.',
        );
        setHasError(true);
        return;
      }

      if (statusCode === 404) {
        setErrorMessage(
          'Onboarding API is not available yet. Please refresh after backend deployment.',
        );
      } else {
        setErrorMessage(
          err.response?.data?.message
            ?? 'We could not determine your organization onboarding status. Please try again.',
        );
      }
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [keycloak, isOnboardingRoute]);

  useEffect(() => {
    if (!initialized || !keycloak.authenticated || !keycloak.token) {
      return;
    }

    if (isPlatformAdmin) {
      setLoading(false);
      setHasError(false);
      return;
    }

    void fetchStatus();
  }, [initialized, keycloak.authenticated, keycloak.token, fetchStatus, isPlatformAdmin]);

  // Trigger Keycloak SSO redirect when unauthenticated, instead of bouncing
  // to a /login page (which no longer exists in this app). If a Keycloak
  // session is already present in the browser, the user lands back here
  // silently authenticated.
  const loginRedirectingRef = useRef(false);
  useEffect(() => {
    if (!initialized) return;
    if (keycloak.authenticated) return;
    if (loginRedirectingRef.current) return;
    loginRedirectingRef.current = true;
    void keycloak.login({ redirectUri: window.location.href });
  }, [initialized, keycloak]);

  if (!initialized || !keycloak.authenticated || !keycloak.token) {
    return <Loader />;
  }

  if (isPlatformAdmin) {
    if (isOnboardingRoute) {
      return <Navigate to={ROUTES.HOME} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (loading) {
    return <Loader />;
  }

  if (hasError || !status) {
    return <ErrorState onRetry={() => void fetchStatus()} message={errorMessage} />;
  }

  if (status.state === 'approved') {
    if (status.requiresReauthentication) {
      return <Navigate to={ROUTES.WAITING_APPROVAL} replace />;
    }
    if (isOnboardingRoute) {
      // Land on the role-appropriate home, not the asset dashboard. HomeDashboard
      // resolves the right view per role (org admin, member, platform admin).
      return <Navigate to={ROUTES.HOME} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (status.state === 'no_organization') {
    if (location.pathname !== ROUTES.NO_ORGANIZATION && location.pathname !== ROUTES.ORG_REGISTRATION) {
      return <Navigate to={ROUTES.NO_ORGANIZATION} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (status.state === 'rejected') {
    if (location.pathname !== ROUTES.REJECTED_ORGANIZATION) {
      return <Navigate to={ROUTES.REJECTED_ORGANIZATION} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (location.pathname !== ROUTES.WAITING_APPROVAL) {
    return <Navigate to={ROUTES.WAITING_APPROVAL} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
